// Copyright ©️ 2026 eVolve MEP, LLC

/* oxlint-disable evolve/no-param-reassign */

import { FLAT_BLACK_FILLER, FLAT_WHITE_FILLER, type HexTriplet, isHexTriplet } from '@evolvemep/api-ts';
import { flushSync } from 'react-dom';
import { createRoot } from 'react-dom/client';

import {
  FONT_SIZE_CHAR_LIMIT,
  FONT_SIZE_MAX,
  FONT_SIZE_MIN,
  MARKUP_SIZE_CHAR_LIMIT,
  MARKUP_SIZE_MAX,
  MARKUP_SIZE_MIN,
  OPACITY_CHAR_LIMIT,
  OPACITY_MAX,
  OPACITY_MIN,
  PT_PER_INCH,
} from 'components/Autodesk/extensions/MarkupModeExtension/constants';
import type { PropertiesPanel } from 'components/Autodesk/extensions/MarkupModeExtension/PropertiesPanel';
import { FillColorField } from 'components/Autodesk/extensions/MarkupModeExtension/PropertyPanelFields/FillColorField';
import {
  isSupportedFontFamily,
  type SupportedFontFamily,
} from 'components/Autodesk/extensions/MarkupModeExtension/PropertyPanelFields/fontConstants';
import { FontFamilyField } from 'components/Autodesk/extensions/MarkupModeExtension/PropertyPanelFields/FontFamilyField';
import { FontSizeField } from 'components/Autodesk/extensions/MarkupModeExtension/PropertyPanelFields/FontSizeField';
import { FontStyleField } from 'components/Autodesk/extensions/MarkupModeExtension/PropertyPanelFields/FontStyleField';
import { StampSizeField } from 'components/Autodesk/extensions/MarkupModeExtension/PropertyPanelFields/StampSizeField';
import { StrokeColorField } from 'components/Autodesk/extensions/MarkupModeExtension/PropertyPanelFields/StrokeColorField';
import { StrokeWidthField } from 'components/Autodesk/extensions/MarkupModeExtension/PropertyPanelFields/StrokeWidthField';
import type { SelectionToolbar } from 'components/Autodesk/extensions/MarkupModeExtension/SelectionToolbar';
import { isNotNil } from 'helpers/isNotNil';
import { strongKeys } from 'helpers/strongEntries';

const { Autodesk } = window;

/**
 * The common style properties shared across markup tools.
 *
 *  Notes:
 *  - Widths and sizes are stored in pts and converted to inches (divided by 72) when applied to the SVG canvas (Autodesk uses inches internally).
 *  - Opacities are from 0-100 and are divided by 100 when applied to the SVG canvas.
 */
export type MarkupStyleState = {
  /** Integer of at least 1. */
  strokeWidth?: number;
  strokeColor?: HexTriplet;
  /** Integer between 0 - 100. */
  strokeOpacity?: number;
  fillColor?: HexTriplet;
  /** Integer between 0 - 100. */
  fillOpacity?: number;
  /** Integer of at least 1. */
  fontSize?: number;
  fontFamily?: SupportedFontFamily;
  /** Maps to 'font-weight' property, renamed as a boolean for convenience. */
  isBold?: boolean;
  /** Maps to 'font-style' property, renamed as a boolean for convenience. */
  isItalic?: boolean;

  // Non-standard properties, not directly supported by Autodesk:

  /** The height of a stamp in pts. Specific to stamp tools. */
  stampSize?: number;
};

/** A `<input type="number">` for stroke width. */
type StrokeWidthFieldConfig = {
  property: 'stroke-width';
  label: string;
};

/** A paired color picker + opacity number input for stroke color/opacity. */
type StrokeColorFieldConfig = {
  property: 'stroke-color';
  colorLabel: string;
  opacityLabel: string;
};

/** A paired color picker + opacity number input for fill color/opacity. */
type FillColorFieldConfig = {
  property: 'fill-color';
  colorLabel: string;
  opacityLabel: string;
};

/** A `<input type="number">` for font size. */
type FontSizeFieldConfig = {
  property: 'font-size';
  label: string;
};

/** A `<select>` for font family. */
type FontFamilyFieldConfig = {
  property: 'font-family';
  label: string;
};

/** A paired Bold/Italic toggle button group for font-weight + font-style. */
type FontStyleFieldConfig = {
  property: 'font-style';
  label: string;
};

/** A `<input type="number">` for stamp size (height in points). */
type StampSizeFieldConfig = {
  property: 'stamp-size';
  label: string;
};

type PropertyFieldConfig =
  | StrokeWidthFieldConfig
  | StrokeColorFieldConfig
  | FillColorFieldConfig
  | FontSizeFieldConfig
  | FontFamilyFieldConfig
  | FontStyleFieldConfig
  | StampSizeFieldConfig;

/** A titled section (`<div class="field-group">`) containing one or more property fields. */
type PanelSectionConfig = {
  heading: string;
  fields: PropertyFieldConfig[];
};

/** The full config that describes a tool's properties panel. */
export type PanelConfig = {
  panelTitle: string;
  sections: PanelSectionConfig[];
};

/** Shared dependencies injected into every markup tool. */
export type MarkupToolContext = {
  viewer: Autodesk.Viewing.GuiViewer3D;
  /** Autodesk's core markup extension. */
  markupExtension: Autodesk.Viewing.Extensions.Markups.Core.MarkupsCore;
  /** Callback to deactivate the previously activated tool before activating a new one. */
  beforeActivateTool: (toolType: string) => void;
  /** Single shared properties panel. Each tool swaps its content in when it activates. */
  propertiesPanel: PropertiesPanel;
  /** Floating action toolbar shown above/below the currently selected markup. */
  selectionToolbar: SelectionToolbar;
};

type MarkupStyle = Autodesk.Viewing.Extensions.Markups.Core.Markup['style'];

/**
 * Maps each MarkupStyleState key (excluding the non-Autodesk 'stampSize') to its corresponding key in MarkupStyle.
 */
type StateKeyToStyleKey = {
  strokeWidth: 'stroke-width';
  strokeColor: 'stroke-color';
  strokeOpacity: 'stroke-opacity';
  fillColor: 'fill-color';
  fillOpacity: 'fill-opacity';
  fontSize: 'font-size';
  fontFamily: 'font-family';
  isBold: 'font-weight';
  isItalic: 'font-style';
};

/**
 * Describes the translation between a markup's SVG style property and its associated state.
 */
type MarkupStyleDescriptor<K extends keyof StateKeyToStyleKey> = {
  /** The corresponding key in the MarkupStyle SVG style object. */
  styleKey: StateKeyToStyleKey[K];
  /**
   * Converts an SVG style property value to a MarkupStyleState value (e.g. multiplies svg['stroke-width'] by 72 to
   * convert from inches to pts for state.strokeWidth).
   */
  fromStylePropertyValue: (v: NonNullable<MarkupStyle[StateKeyToStyleKey[K]]>) => NonNullable<MarkupStyleState[K]>;
  /**
   * Converts a MarkupStyleState value to an SVG style property value (e.g. divides state.strokeWidth by 72 to convert
   * from pts to inches for svg['stroke-width']).
   */
  toStylePropertyValue: (v: NonNullable<MarkupStyleState[K]>) => NonNullable<MarkupStyle[StateKeyToStyleKey[K]]>;
};

type MarkupStyleDescriptors = {
  [K in keyof StateKeyToStyleKey]: MarkupStyleDescriptor<K>;
};

/**
 * The single source of truth for mapping the Autodesk-supported MarkupStyleState properties to and from a markup's
 * SVG style properties and their related state values.
 *
 * Note: `stampSize` is intentionally omitted. It is a non-standard, stamp-specific property handled in StampTool.
 */
const AUTODESK_STYLE_DESCRIPTORS: Readonly<MarkupStyleDescriptors> = {
  strokeWidth: {
    styleKey: 'stroke-width',
    fromStylePropertyValue: (valueInInches) => Math.round(valueInInches * PT_PER_INCH),
    toStylePropertyValue: (valueInPts) => valueInPts / PT_PER_INCH,
  },
  strokeColor: {
    styleKey: 'stroke-color',
    fromStylePropertyValue: (hexCode) => (isHexTriplet(hexCode) ? hexCode : FLAT_BLACK_FILLER),
    toStylePropertyValue: (hexCode) => hexCode,
  },
  strokeOpacity: {
    styleKey: 'stroke-opacity',
    fromStylePropertyValue: (opacityFraction) => Math.round(opacityFraction * 100), // 0.2 => 20
    toStylePropertyValue: (opacityPercent) => opacityPercent / 100, // 20 => 0.2
  },
  fillColor: {
    styleKey: 'fill-color',
    fromStylePropertyValue: (hexCode) => (isHexTriplet(hexCode) ? hexCode : FLAT_WHITE_FILLER),
    toStylePropertyValue: (hexCode) => hexCode,
  },
  fillOpacity: {
    styleKey: 'fill-opacity',
    fromStylePropertyValue: (opacityFraction) => Math.round(opacityFraction * 100), // 0.2 => 20
    toStylePropertyValue: (opacityPercent) => opacityPercent / 100, // 20 => 0.2
  },
  fontSize: {
    styleKey: 'font-size',
    fromStylePropertyValue: (valueInInches) => Math.round(valueInInches * PT_PER_INCH),
    toStylePropertyValue: (valueInPts) => valueInPts / PT_PER_INCH,
  },
  fontFamily: {
    styleKey: 'font-family',
    fromStylePropertyValue: (fontFamily) => (isSupportedFontFamily(fontFamily) ? fontFamily : 'arial'),
    toStylePropertyValue: (fontFamily) => fontFamily,
  },
  isBold: {
    styleKey: 'font-weight',
    fromStylePropertyValue: (fontWeight) => fontWeight === 'bold',
    toStylePropertyValue: (isBold) => (isBold ? 'bold' : 'normal'),
  },
  isItalic: {
    styleKey: 'font-style',
    fromStylePropertyValue: (fontStyle) => fontStyle === 'italic',
    toStylePropertyValue: (isItalic) => (isItalic ? 'italic' : 'normal'),
  },
};

const formatHexColor = (color: HexTriplet) => color.replace('#', '').toUpperCase();

/**
 * The base class for all markup tools,responsible for managing the shared properties panel and syncing style state
 * between the panel, the currently selected markup, and the edit mode style.
 *
 * Each specific markup tool (e.g. rectangle, ellipse, text, stamp) extends this class and defines the specific style
 * properties relevant to that tool, as well as the appropriate edit mode and toolbar button icon.
 */
export abstract class BaseMarkupTool {
  abstract readonly type: string;

  private button: Autodesk.Viewing.UI.Button | null = null;
  readonly viewer: Autodesk.Viewing.GuiViewer3D;
  protected readonly markupExtension: Autodesk.Viewing.Extensions.Markups.Core.MarkupsCore;
  /** Callback to deactivate the previously activated tool. */
  private readonly beforeActivateTool: (toolType: string) => void;
  /** Shared properties panel managed by MarkupExtension. Content is swapped when this tool activates. */
  protected readonly propertiesPanel: PropertiesPanel;
  protected readonly selectionToolbar: SelectionToolbar;
  private readonly createPropertyFieldMap: Record<
    PropertyFieldConfig['property'],
    (field: PropertyFieldConfig) => HTMLElement | null
  > = {
    'stroke-width': (field) => this.createStrokeWidthField(field),
    'stroke-color': (field) => this.createStrokeColorField(field),
    'fill-color': (field) => this.createFillColorField(field),
    'font-size': (field) => this.createFontSizeField(field),
    'font-family': (field) => this.createFontFamilyField(field),
    'font-style': (field) => this.createFontStyleField(field),
    'stamp-size': (field) => this.createStampSizeField(field),
  };

  /**
   * Style state for this tool, initialized from the defaults passed to the constructor.
   *
   * Note: If extending the state beyond the default MarkupStyleState properties,
   * then the updatePropertyPanelForSelection method will need to be overridden.
   * See StampTool.js for an example.
   * */
  private _state: MarkupStyleState;
  protected get state(): MarkupStyleState {
    return this._state;
  }
  private set state(value: MarkupStyleState) {
    this._state = value;
  }
  /** Defaults used to initialize the style state and reset state as needed. */
  readonly defaults: Readonly<MarkupStyleState>;

  /** Lazily-built content element for the properties panel. */
  private _toolContent: HTMLElement | null = null;
  protected get toolContent(): HTMLElement {
    this._toolContent ??= this.createToolContent();
    return this._toolContent;
  }

  protected constructor(
    context: MarkupToolContext,
    /** Used to establish what state properties this tool manages and their default values. */
    defaults: MarkupStyleState,
  ) {
    this.viewer = context.viewer;
    this.markupExtension = context.markupExtension;
    this.beforeActivateTool = context.beforeActivateTool;
    this.propertiesPanel = context.propertiesPanel;
    this.selectionToolbar = context.selectionToolbar;
    this.defaults = structuredClone(defaults);
    this._state = structuredClone(defaults);
  }

  // ---- Abstract interfaces ----

  /** Describes the properties panel layout for this tool. Used to build the panel HTML and wire all inputs. */
  protected abstract getPanelConfig(): PanelConfig;

  /**
   * Activates the appropriate Autodesk edit mode for this tool.
   * Called within `activate()` when the current edit mode type does not match.
   */
  protected abstract activateEditMode(): void;

  /** Raw SVG string rendered as the toolbar button's icon. */
  protected abstract readonly buttonIcon: string;

  /** Tooltip text shown on the toolbar button. */
  protected abstract readonly buttonTooltip: string;

  // ---- State management ----

  protected setStyleState(updatedStyle: MarkupStyleState) {
    this.state = structuredClone(updatedStyle);
  }

  protected resetState() {
    this.state = structuredClone(this.defaults);
  }

  // ---- Panel builder ----

  /** Syncs the current style state back into all panel inputs. Called whenever state changes. */
  applyPropertiesToPanel() {
    this.getPanelConfig()
      .sections.flatMap((section) => section.fields)
      .forEach((field) => {
        this.applyFieldToPanel(field);
      });
  }

  private applyFieldToPanel(field: PropertyFieldConfig) {
    switch (field.property) {
      case 'stroke-width': {
        const input = document.getElementById(`${this.type}-stroke-width`) as HTMLInputElement | null;
        if (input) input.value = String(this.state.strokeWidth ?? '');
        break;
      }
      case 'stroke-color': {
        const colorInput = document.getElementById(`${this.type}-stroke-color`) as HTMLInputElement | null;
        const colorLabel = document.getElementById(`${this.type}-stroke-color-label`) as HTMLElement | null;
        const opacityInput = document.getElementById(`${this.type}-stroke-opacity`) as HTMLInputElement | null;
        if (colorInput && this.state.strokeColor) colorInput.value = this.state.strokeColor;
        if (colorLabel && this.state.strokeColor) colorLabel.textContent = formatHexColor(this.state.strokeColor);
        if (opacityInput) opacityInput.value = String(this.state.strokeOpacity ?? '');
        break;
      }
      case 'fill-color': {
        const colorInput = document.getElementById(`${this.type}-fill-color`) as HTMLInputElement | null;
        const colorLabel = document.getElementById(`${this.type}-fill-color-label`) as HTMLElement | null;
        const opacityInput = document.getElementById(`${this.type}-fill-opacity`) as HTMLInputElement | null;
        if (colorInput && this.state.fillColor) colorInput.value = this.state.fillColor;
        if (colorLabel && this.state.fillColor) colorLabel.textContent = formatHexColor(this.state.fillColor);
        if (opacityInput) opacityInput.value = String(this.state.fillOpacity ?? '');
        break;
      }
      case 'font-size': {
        const input = document.getElementById(`${this.type}-font-size`) as HTMLInputElement | null;
        if (input) input.value = String(this.state.fontSize ?? '');
        break;
      }
      case 'font-family': {
        const select = document.getElementById(`${this.type}-font-family`) as HTMLSelectElement | null;
        if (select && this.state.fontFamily) {
          select.value = this.state.fontFamily;
          select.style.fontFamily = this.state.fontFamily;
        }
        break;
      }
      case 'stamp-size': {
        const input = document.getElementById(`${this.type}-stamp-size`) as HTMLInputElement | null;
        if (input) input.value = String(this.state.stampSize ?? '');
        break;
      }
      case 'font-style': {
        const boldButton = document.getElementById(`${this.type}-font-weight`) as HTMLButtonElement | null;
        const italicButton = document.getElementById(`${this.type}-font-style`) as HTMLButtonElement | null;
        if (boldButton) boldButton.classList.toggle('active', this.state.isBold ?? false);
        if (italicButton) italicButton.classList.toggle('active', this.state.isItalic ?? false);
        break;
      }
    }
  }

  // ---- Field wiring helpers ----

  /**
   * Wires a number input with event listeners to handle user input in an "undo-friendly" way:
   * - `input` event: visually previews the change on the selected markup (no state change).
   * - `blur` event: commits the new value to state and calls applyStyles().
   *
   * Assumes the input is for integers only.
   *
   * Also attaches stopViewerMouseCapture to the input element.
   */
  private wireIntegerInput<K extends keyof StateKeyToStyleKey>({
    integerInput,
    getValue,
    setValue,
    stateKey,
    min,
    max,
    charLimit,
  }: {
    integerInput: HTMLInputElement;
    getValue: () => number | undefined;
    setValue: (value: number) => void;
    stateKey: K;
    min: number;
    max: number;
    charLimit?: number;
  }) {
    const { styleKey, toStylePropertyValue } = AUTODESK_STYLE_DESCRIPTORS[stateKey];
    integerInput.addEventListener('keydown', (e) => {
      // Allow control keys (backspace, delete, arrows, tab, etc.) but block anything that isn't a digit.
      if (e.key.length === 1 && !/^\d$/.test(e.key)) {
        e.preventDefault();
      }
    });
    integerInput.addEventListener('input', () => {
      if (charLimit !== undefined) {
        // Strip leading zeros and enforce character limit.
        const stripped = integerInput.value.replace(/^0+(?=\d)/, '');
        integerInput.value = stripped.length > charLimit ? stripped.slice(0, charLimit) : stripped;
      }
      const value = Math.round(Number(integerInput.value));
      if (Number.isNaN(value)) return;
      const selectedMarkup = this.markupExtension.getSelection();
      if (selectedMarkup) {
        const updatedStyle = structuredClone(selectedMarkup.style);
        const newValue = Math.min(max, Math.max(min, value));
        updatedStyle[styleKey] = toStylePropertyValue(newValue as NonNullable<MarkupStyleState[K]>);
        // Visually preview the change for the selected markup. Doesn't get applied to state yet.
        selectedMarkup.setStyle(updatedStyle);
        // Update the selection box in case a style change would affect the size of the markup.
        // This is only necessary because we're previewing the change by directly setting the
        // markup's style instead of using editMode.setStyle().
        this.markupExtension.editFrame.setMarkup(selectedMarkup);
        this.selectionToolbar.reposition(selectedMarkup);
      }
    });
    integerInput.addEventListener('blur', () => {
      const value = Math.round(Number(integerInput.value));
      if (Number.isNaN(value)) return;
      const initialValue = getValue();
      const newValue = Math.min(max, Math.max(min, value));
      // Hack: Autodesk's action manager uses the value from the markup's style itself when creating a new undo action.
      // Since we are changing the markup's style on every input, we'll want to change it back to
      // the desired state just before committing the change.
      // This gives the user visual feedback for incremental input changes while still allowing them
      // to undo the changes in a single step.
      const selectedMarkup = this.markupExtension.getSelection();
      if (selectedMarkup && isNotNil(initialValue)) {
        const updatedStyle = structuredClone(selectedMarkup.style);
        updatedStyle[styleKey] = toStylePropertyValue(initialValue as NonNullable<MarkupStyleState[K]>);
        selectedMarkup.setStyle(updatedStyle);
      }
      setValue(newValue);
      integerInput.value = newValue.toString();
      this.applyStyles();
    });
  }

  /**
   * Wires a color input with event listeners to handle user input in an "undo-friendly" way:
   * - `input` event: visually previews the color change on the selected markup (no state change).
   * - `change` event: commits the new color to state and then calls applyStyles().
   */
  private wireColorInput({
    colorInput,
    colorLabel,
    getValue,
    setValue,
    styleKey,
  }: {
    colorInput: HTMLInputElement;
    colorLabel: HTMLElement;
    getValue: () => HexTriplet | undefined;
    setValue: (value: HexTriplet) => void;
    styleKey: keyof MarkupStyle;
  }) {
    colorInput.addEventListener('input', () => {
      if (!isHexTriplet(colorInput.value)) return;
      colorLabel.textContent = formatHexColor(colorInput.value);
      const selectedMarkup = this.markupExtension.getSelection();
      if (selectedMarkup) {
        const updatedStyle = structuredClone(selectedMarkup.style);
        (updatedStyle[styleKey] as HexTriplet) = colorInput.value;
        selectedMarkup.setStyle(updatedStyle);
      }
    });
    colorInput.addEventListener('change', () => {
      if (!isHexTriplet(colorInput.value)) return;
      const initialColor = getValue();
      // Hack: Autodesk's action manager uses the value from the markup's style itself when creating a new undo action.
      // Since we are changing the markup's style on every input, we'll want to change it back to
      // the desired state just before committing the change.
      // This gives the user visual feedback for incremental input changes while still allowing them
      // to undo the changes in a single step.
      const selectedMarkup = this.markupExtension.getSelection();
      if (selectedMarkup && isNotNil(initialColor)) {
        const updatedStyle = structuredClone(selectedMarkup.style);
        (updatedStyle[styleKey] as HexTriplet) = initialColor;
        selectedMarkup.setStyle(updatedStyle);
      }
      setValue(colorInput.value);
      colorLabel.textContent = formatHexColor(colorInput.value);
      this.applyStyles();
    });
  }

  /** Builds the content HTMLElement for this tool's section of the shared properties panel. */
  private createToolContent(): HTMLElement {
    const config = this.getPanelConfig();

    const container = document.createElement('div');
    container.className = 'content-container';

    for (const section of config.sections) {
      const group = document.createElement('div');
      group.className = 'field-group';
      group.innerHTML = `<h2>${section.heading}</h2>`;
      for (const field of section.fields) {
        const fieldElement = this.createPropertyFieldMap[field.property](field);
        if (fieldElement) group.appendChild(fieldElement);
      }
      container.appendChild(group);
    }

    return container;
  }

  private createStrokeWidthField(field: PropertyFieldConfig): HTMLElement | null {
    if (field.property !== 'stroke-width') return null;
    const id = `${this.type}-stroke-width`;
    const wrapper = document.createElement('div');
    const root = createRoot(wrapper);
    flushSync(() => {
      root.render(<StrokeWidthField id={id} label={field.label} value={String(this.state.strokeWidth ?? '')} />);
    });
    const fieldEl = wrapper.firstElementChild as HTMLElement;
    const input = fieldEl.querySelector<HTMLInputElement>(`#${id}`) as HTMLInputElement;
    this.wireIntegerInput({
      integerInput: input,
      getValue: () => this.state.strokeWidth,
      setValue: (value) => {
        this.state.strokeWidth = value;
      },
      stateKey: 'strokeWidth',
      min: MARKUP_SIZE_MIN,
      max: MARKUP_SIZE_MAX,
      charLimit: MARKUP_SIZE_CHAR_LIMIT,
    });
    return fieldEl;
  }

  private createStrokeColorField(field: PropertyFieldConfig): HTMLElement | null {
    if (field.property !== 'stroke-color') return null;
    const colorId = `${this.type}-stroke-color`;
    const labelId = `${this.type}-stroke-color-label`;
    const opacityId = `${this.type}-stroke-opacity`;
    const group = document.createElement('div');
    const defaultStrokeColor = FLAT_BLACK_FILLER;
    const root = createRoot(group);
    flushSync(() => {
      root.render(
        <StrokeColorField
          colorId={colorId}
          labelId={labelId}
          opacityId={opacityId}
          colorLabel={field.colorLabel}
          opacityLabel={field.opacityLabel}
          colorDisplayValue={formatHexColor(this.state.strokeColor ?? defaultStrokeColor)}
          opacityValue={String(this.state.strokeOpacity ?? '')}
        />,
      );
    });
    const groupEl = group.firstElementChild as HTMLElement;
    const colorInput = groupEl.querySelector<HTMLInputElement>(`#${colorId}`) as HTMLInputElement;
    const colorLabel = groupEl.querySelector<HTMLElement>(`#${labelId}`) as HTMLElement;
    const opacityInput = groupEl.querySelector<HTMLInputElement>(`#${opacityId}`) as HTMLInputElement;
    this.wireColorInput({
      colorInput,
      colorLabel,
      getValue: () => this.state.strokeColor,
      setValue: (value) => {
        if (!isHexTriplet(value)) return;
        this.state.strokeColor = value;
      },
      styleKey: 'stroke-color',
    });
    opacityInput.value = String(this.state.strokeOpacity ?? '');
    this.wireIntegerInput({
      integerInput: opacityInput,
      getValue: () => this.state.strokeOpacity,
      setValue: (value) => {
        this.state.strokeOpacity = value;
      },
      stateKey: 'strokeOpacity',
      min: OPACITY_MIN,
      max: OPACITY_MAX,
      charLimit: OPACITY_CHAR_LIMIT,
    });
    return groupEl;
  }

  private createFillColorField(field: PropertyFieldConfig): HTMLElement | null {
    if (field.property !== 'fill-color') return null;
    const colorId = `${this.type}-fill-color`;
    const labelId = `${this.type}-fill-color-label`;
    const opacityId = `${this.type}-fill-opacity`;
    const group = document.createElement('div');
    const defaultFillColor = '#FFFFFF' as HexTriplet;
    const root = createRoot(group);
    flushSync(() => {
      root.render(
        <FillColorField
          colorId={colorId}
          labelId={labelId}
          opacityId={opacityId}
          colorLabel={field.colorLabel}
          opacityLabel={field.opacityLabel}
          colorDisplayValue={formatHexColor(this.state.fillColor ?? defaultFillColor)}
          colorValue={this.state.fillColor ?? '#ffffff'}
          opacityValue={String(this.state.fillOpacity ?? '')}
        />,
      );
    });
    const groupEl = group.firstElementChild as HTMLElement;
    const colorInput = groupEl.querySelector<HTMLInputElement>(`#${colorId}`) as HTMLInputElement;
    const colorLabel = groupEl.querySelector<HTMLElement>(`#${labelId}`) as HTMLElement;
    const opacityInput = groupEl.querySelector<HTMLInputElement>(`#${opacityId}`) as HTMLInputElement;
    this.wireColorInput({
      colorInput,
      colorLabel,
      getValue: () => this.state.fillColor,
      setValue: (value) => {
        if (!isHexTriplet(value)) return;
        this.state.fillColor = value;
      },
      styleKey: 'fill-color',
    });
    opacityInput.value = String(this.state.fillOpacity ?? '');
    this.wireIntegerInput({
      integerInput: opacityInput,
      getValue: () => this.state.fillOpacity,
      setValue: (value) => {
        this.state.fillOpacity = value;
      },
      stateKey: 'fillOpacity',
      min: OPACITY_MIN,
      max: OPACITY_MAX,
      charLimit: OPACITY_CHAR_LIMIT,
    });
    return groupEl;
  }

  private createFontSizeField(field: PropertyFieldConfig): HTMLElement | null {
    if (field.property !== 'font-size') return null;
    const id = `${this.type}-font-size`;
    const wrapper = document.createElement('div');
    const root = createRoot(wrapper);
    flushSync(() => {
      root.render(<FontSizeField id={id} label={field.label} value={String(this.state.fontSize ?? '')} />);
    });
    const fieldEl = wrapper.firstElementChild as HTMLElement;
    const input = fieldEl.querySelector<HTMLInputElement>(`#${id}`) as HTMLInputElement;
    this.wireIntegerInput({
      integerInput: input,
      getValue: () => this.state.fontSize,
      setValue: (value) => {
        this.state.fontSize = value;
      },
      stateKey: 'fontSize',
      min: FONT_SIZE_MIN,
      max: FONT_SIZE_MAX,
      charLimit: FONT_SIZE_CHAR_LIMIT,
    });
    return fieldEl;
  }

  private createFontFamilyField(field: PropertyFieldConfig): HTMLElement | null {
    if (field.property !== 'font-family') return null;
    const id = `${this.type}-font-family`;
    const wrapper = document.createElement('div');
    const root = createRoot(wrapper);
    flushSync(() => {
      root.render(<FontFamilyField id={id} label={field.label} value={this.state.fontFamily ?? ''} />);
    });
    const fieldEl = wrapper.firstElementChild as HTMLElement;
    const select = fieldEl.querySelector<HTMLSelectElement>(`#${id}`) as HTMLSelectElement;
    select.addEventListener('input', () => {
      if (!isSupportedFontFamily(select.value)) return;
      this.state.fontFamily = select.value;
      select.style.fontFamily = select.value;
      this.applyStyles();
    });
    return fieldEl;
  }

  private createStampSizeField(field: PropertyFieldConfig): HTMLElement | null {
    if (field.property !== 'stamp-size') return null;
    const id = `${this.type}-stamp-size`;
    const wrapper = document.createElement('div');
    const root = createRoot(wrapper);
    flushSync(() => {
      root.render(<StampSizeField id={id} label={field.label} value={String(this.state.stampSize ?? '')} />);
    });
    const fieldEl = wrapper.firstElementChild as HTMLElement;
    const input = fieldEl.querySelector<HTMLInputElement>(`#${id}`) as HTMLInputElement;
    input.value = String(this.state.stampSize ?? '');
    input.addEventListener('input', () => {
      const next = Number(input.value);
      if (Number.isNaN(next)) return;
      this.state.stampSize = Math.max(1, next);
      this.applyStyles();
    });
    return fieldEl;
  }

  private createFontStyleField(field: PropertyFieldConfig): HTMLElement | null {
    if (field.property !== 'font-style') return null;
    const boldId = `${this.type}-font-weight`;
    const italicId = `${this.type}-font-style`;
    const wrapper = document.createElement('div');
    const root = createRoot(wrapper);
    flushSync(() => {
      root.render(
        <FontStyleField
          boldId={boldId}
          italicId={italicId}
          label={field.label}
          isBold={this.state.isBold ?? false}
          isItalic={this.state.isItalic ?? false}
        />,
      );
    });
    const fieldEl = wrapper.firstElementChild as HTMLElement;
    const boldButton = fieldEl.querySelector<HTMLButtonElement>(`#${boldId}`) as HTMLButtonElement;
    const italicButton = fieldEl.querySelector<HTMLButtonElement>(`#${italicId}`) as HTMLButtonElement;
    boldButton.addEventListener('click', () => {
      this.state.isBold = !this.state.isBold;
      boldButton.classList.toggle('active', this.state.isBold);
      boldButton.blur();
      this.applyStyles();
    });
    italicButton.addEventListener('click', () => {
      this.state.isItalic = !this.state.isItalic;
      italicButton.classList.toggle('active', this.state.isItalic);
      italicButton.blur();
      this.applyStyles();
    });
    return fieldEl;
  }

  updatePropertyPanelForStyle(style: MarkupStyle) {
    // Get the updated state value from the markup's style.
    const updatedStyle: MarkupStyleState = {};
    // This exists solely to help TypeScript resolve `updatedStyle[K]` with the correct type.
    const applyStyleToState = <K extends keyof StateKeyToStyleKey>(stateKey: K) => {
      const { styleKey, fromStylePropertyValue } = AUTODESK_STYLE_DESCRIPTORS[stateKey];
      const svgValue = style[styleKey];
      updatedStyle[stateKey] = isNotNil(svgValue) ? fromStylePropertyValue(svgValue) : this.defaults[stateKey];
    };
    for (const stateKey of strongKeys(AUTODESK_STYLE_DESCRIPTORS)) {
      if (this.defaults[stateKey] === undefined) continue;
      applyStyleToState(stateKey);
    }
    this.setStyleState(updatedStyle);
    this.applyPropertiesToPanel();
  }

  /** Updates the values in the properties panel to match the currently selected markup or otherwise revert to the default values. */
  updatePropertyPanelForSelection() {
    const selection = this.markupExtension.getSelection();
    if (!selection || selection.type !== this.type) {
      this.resetState();
      this.applyPropertiesToPanel();
    } else {
      this.updatePropertyPanelForStyle(selection.style);
    }
  }

  /**
   * Callback to be invoked when markup is selected of the given tool's type.
   * */
  onMarkupSelected() {
    /* no-op */
  }

  /** Called when the tool's toolbar button is clicked or when a markup of the tool's type is selected. */
  activate() {
    this.beforeActivateTool(this.type);
    // Autodesk's markup extension handles swapping edit modes when a markup is selected, but we still need to change the edit mode when clicking the tool's button.
    if (this.markupExtension.editMode.type !== this.type) {
      this.activateEditMode();
    }
    this.propertiesPanel.setContent(this.getPanelConfig().panelTitle, this.toolContent);
    this.updatePropertyPanelForSelection();
    // Selecting an existing markup should update the style to be used by the markup tool without affecting the undo history.
    this.applyStyles(false);
    this.button?.setState(Autodesk.Viewing.UI.Button.State.ACTIVE);
  }

  deactivate() {
    this.button?.setState(Autodesk.Viewing.UI.Button.State.INACTIVE);
  }

  createButton() {
    this.button = new Autodesk.Viewing.UI.Button(`toolbar-markup-${this.type}`);
    this.button.onClick = () => {
      this.activate();
      this.propertiesPanel.setVisible(true);
    };
    this.button.icon.innerHTML = this.buttonIcon;
    this.button.setToolTip(this.buttonTooltip);
    this.button.addClass('custom-icon-toolbar-button');

    return this.button;
  }

  protected applyStyles(addToHistory = true) {
    const baseStyle = this.markupExtension.editMode.getStyle();
    const nextStyle: MarkupStyle = structuredClone(baseStyle);

    // Get the updated markup style from the current state.
    // This exists to help TypeScript to keep K specific so toStylePropertyValue is called with the right type.
    const applyStateToStyle = <K extends keyof StateKeyToStyleKey>(stateKey: K) => {
      const value = this.state[stateKey];
      if (value === undefined) return;
      const { toStylePropertyValue, styleKey } = AUTODESK_STYLE_DESCRIPTORS[stateKey];
      nextStyle[styleKey] = toStylePropertyValue(value);
    };
    for (const stateKey of strongKeys(AUTODESK_STYLE_DESCRIPTORS)) {
      applyStateToStyle(stateKey);
    }

    // Directly setting editMode.style will only change what style is used when creating the next markup.
    // Calling editMode.setStyle() does the same, in addition to adding an action to the undo stack if markup is selected.
    // https://github.com/wallabyway/aps-markup-measure-extensions/blob/master/Markup/core/edit-modes/EditMode.js
    if (addToHistory) {
      this.markupExtension.editMode.setStyle(nextStyle);
    } else {
      this.markupExtension.editMode.style = nextStyle;
    }
  }
}
